import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { BsBoxArrowUpRight } from "react-icons/bs";
import { BiArrowBack } from "react-icons/bi";
import { FaGithub } from "react-icons/fa";
import { toAbsoluteUrl } from "../utils/url";

function ProjectDetails() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Debug logging
  console.log('ProjectDetails mounted with ID:', id);
  console.log('Current URL:', window.location.href);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!id) {
          setError("Project ID is required");
          setLoading(false);
          return;
        }
        
        const ref = doc(db, "projects", id);
        const snap = await getDoc(ref);
        if (mounted && snap.exists()) {
          setProject({ id: snap.id, ...snap.data() });
        } else if (mounted) {
          setError("Project not found");
        }
      } catch (e) {
        console.error("Error loading project:", e);
        if (mounted) setError("Unable to load project. Please try again.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="progress-bar mx-auto mb-4" />
          <p className="text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops!</h2>
          <p className="text-red-600 mb-6">{error}</p>
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 bg-[#211033] text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors duration-200"
          >
            <BiArrowBack />
            Back to Portfolio
          </Link>
        </div>
      </div>
    );
  }
  if (!project) return null;

  const tags = project.tags || project.tech || [];
  const tech = project.tech || [];
  const liveUrl = project?.liveLink ? toAbsoluteUrl(project.liveLink) : "";

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white text-gray-800">
      <div className="max-w-5xl mx-auto px-6">
        {/* Breadcrumbs */}
        <div className="text-sm text-gray-500 mb-6 flex items-center gap-2">
          <Link to="/portfolio" className="hover:underline">
            Portfolio
          </Link>
          <span>/</span>
          <span className="font-semibold text-gray-700 truncate">
            {project.title}
          </span>
        </div>

        {/* Title + CTA */}
        <div className="flex flex-col xl:flex-row items-start xl:items-start justify-between gap-6 xl:gap-8 mb-8">
          <div className="flex-1 max-w-4xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight leading-tight mb-4">
              {project.title}
            </h1>
            {project.shortDesc && (
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl">
                {project.shortDesc}
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row xl:flex-col items-stretch sm:items-center xl:items-stretch gap-3 w-full xl:w-auto xl:min-w-[200px]">
            {project.githubLink && (
              <a
                href={project.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 font-semibold bg-gray-900 text-white border border-gray-900 rounded-full py-3 px-6 hover:bg-white hover:text-gray-900 ease-in duration-300 shadow-lg text-sm sm:text-base"
              >
                <FaGithub />
                GitHub
              </a>
            )}
            {liveUrl && (
              <a
                href={liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  // Ensure it opens even if something intercepts anchor navigation
                  try {
                    e.preventDefault();
                    const win = window.open(
                      liveUrl,
                      "_blank",
                      "noopener,noreferrer"
                    );
                    if (!win) {
                      // Popup blocked; fallback to direct navigation
                      window.location.href = liveUrl;
                    }
                  } catch (_) {
                    // fallback to default behavior (anchor will navigate)
                  }
                }}
                className="inline-flex items-center justify-center gap-2 font-semibold bg-[#211033] text-white border border-teal-700 rounded-full py-3 px-6 hover:bg-white hover:text-[#211033] ease-in duration-300 shadow-lg text-sm sm:text-base"
              >
                Visit Live
                <BsBoxArrowUpRight />
              </a>
            )}
          </div>
        </div>

        {/* Hero image */}
        {project.imageUrl || project.image ? (
          <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm mb-6 bg-white flex items-center justify-center">
            <img
              src={project.imageUrl || project.image}
              alt={project.title}
              className="w-full max-h-[300px] sm:max-h-[400px] lg:max-h-[520px] object-contain"
            />
          </div>
        ) : null}

        {/* Tags */}
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {tags.slice(0, 8).map((t, i) => (
              <span
                key={i}
                className={`inline-flex items-center gap-2 text-[12px] font-semibold px-3 py-1 rounded-full ${
                  i === 0
                    ? "bg-teal-500 text-white border-teal-500"
                    : "bg-teal-100 text-teal-800 border-teal-200"
                } border shadow-sm`}
              >
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left meta */}
          <div className="lg:col-span-1 space-y-4 order-2 lg:order-1">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <h3 className="text-sm font-extrabold text-gray-900 mb-3">
                Tech Stack
              </h3>
              {tech.length ? (
                <ul className="flex flex-wrap gap-2">
                  {tech.map((s, i) => (
                    <li
                      key={i}
                      className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-800 border border-gray-200"
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-600">Not specified</p>
              )}
            </div>

            {liveUrl && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <h3 className="text-sm font-extrabold text-gray-900 mb-3">
                  Live Link
                </h3>
                <a
                  href={liveUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-teal-700 underline break-all text-sm"
                >
                  {liveUrl}
                </a>
              </div>
            )}
          </div>

          {/* Main description */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
              <h3 className="text-lg font-extrabold text-gray-900 mb-3">
                Overview
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">
                {project.detailedDesc || project.shortDesc}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProjectDetails;
